import type { KeyboardEvent } from 'react';

import { Table as MuiTable, TableBody, TableHead, TableRow } from '@mui/material';

import type { TTableProps, TVMBase } from './types';

/**
 * What: the shared data table (header row from `headCells`, one row per item).
 * Why: one table look and behaviour for every portal list.
 * Constraints: with `onRowClick` a row is a keyboard target (`tabIndex=0`); Enter or Space pressed
 * on the ROW ITSELF runs the click handler. Keys pressed inside a cell (a button, a menu, a
 * checkbox) are left alone (`target !== currentTarget`), so they keep their own behaviour. Space
 * is prevented from scrolling the page. Rows without `onRowClick` get no tabIndex and no handler.
 */
export const Table = <PVM extends TVMBase>({
  data,
  headCells,
  bodyCells,
  onRowClick,
  ...props
}: TTableProps<PVM>) => {
  const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, vm: PVM) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRowClick?.(event, vm);
    }
  };

  return (
    <MuiTable {...props}>
      <TableHead>
        <TableRow>{headCells()}</TableRow>
      </TableHead>

      <TableBody>
        {data.map((vm) => (
          <TableRow
            key={vm.id}
            onClick={(event) => onRowClick?.(event, vm)}
            onKeyDown={onRowClick ? (event) => handleRowKeyDown(event, vm) : undefined}
            tabIndex={onRowClick ? 0 : undefined}
            hover={!!onRowClick}
          >
            {bodyCells(vm)}
          </TableRow>
        ))}
      </TableBody>
    </MuiTable>
  );
};
